import React, { useEffect, useState } from "react";
import { Modal, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { loadStripe } from "@stripe/stripe-js";
import toast from "react-hot-toast";
import "./PropertyPlanUpgradeModal.scss";
import { AgentApi } from "../../../../../utils/api/Agent";
import { LocalStorageHelpers } from "../../../../../utils/helpers/localstorage.helpers";
import { CommonEnums } from "../../../../../enums/common.enums";
import { SubscriptionApi } from "../../../../../utils/api/SubscriptionApi";
import SelectPaymentMethod from "../../create/SelectPaymentMethod";
import Loading from "../../../../loading/Loading";
import TrueRoundIcon from "../../../../../components/icons/TrueRoundIcon";
import DownArrowIcon from "../../../../../components/icons/DownArrowIcon";
// const stripePromise = loadStripe(
//   "pk_test_51SLDDkKqGdfB2DqLCbwi0nn8yeYbM9sLRKKMvx767qNOxQF54GVXLa2NLY0qoKTG4cXv7wjqgN7MYfC2YGYxYTSM00qh69HPXt"
// );

interface Props {
  open: boolean;
  onClose: () => void;
  propertyId: string;
  propertyAddress: string;
  listingTypeKey: string;
  currentPlan: any;
  //   onUpgradePlan?: (newPlan: any) => Promise<void>;
}

const PropertyPlanUpgradeModal: React.FC<Props> = ({
  open,
  onClose,
  propertyId,
  propertyAddress,
  listingTypeKey,
  currentPlan,
  //   onUpgradePlan,
}) => {
  const [plans, setPlans] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [selectedFeatureId, setSelectedFeatureId] = useState("");
  const [detectedCountry, setDetectedCountry] = useState("AU");
  const [agentPermissions, setAgentPermissions] = useState<any>(null);
  const [selectedPlanData, setSelectedPlanData] = useState<any | null>(null);
  const [userWalletBalance, setUserWalletBalance] = useState<number>(10);

  const [isEligibleForTrial, setIsEligibleForTrial] = useState(false);

  useEffect(() => {
    const fetchUserLocation = async () => {
      try {
        const response = await fetch("https://ipapi.co/json/");
        const data = await response.json();
        if (data && data.country_code) setDetectedCountry(data.country_code);
      } catch (error) {
        console.error("Location check failed", error);
      }
    };
    fetchUserLocation();
  }, []);

  const fetchAgentWallet = async (agentId: string) => {
    try {
      const res: any = await AgentApi.getAgentWalletByUserId(agentId);

      if (res?.success && res?.data?.success) {
        const rawBalance = res.data.data?.balance;

        const balance = rawBalance?.$numberDecimal
          ? Number(rawBalance.$numberDecimal)
          : Number(rawBalance ?? 0);

        setUserWalletBalance(isNaN(balance) ? 0 : balance);
      } else {
        setUserWalletBalance(0);
      }
    } catch (error) {
      console.error("Wallet fetch failed", error);
      setUserWalletBalance(0);
    }
  };

  const fetchAgentPermissions = async (agentId: string) => {
    try {
      const res: any = await AgentApi.getAgentPermissionsById(agentId);
      if (res?.data?.success || res?.data) {
        setAgentPermissions(res?.data?.data || res?.data);
      }
    } catch (error) {
      console.error("Permission fetch failed", error);
      setAgentPermissions(null);
    }
  };

  useEffect(() => {
    if (open && listingTypeKey) {
      fetchPlans();

      const user: any = LocalStorageHelpers.getUserData();
      if (user?._id) {
        fetchAgentWallet(user._id);
        fetchAgentPermissions(user._id);
      }
    }

    if (!open) {
      setSelectedPlanData(null);
    }
  }, [open, listingTypeKey]);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const subscriptionType =
        CommonEnums.subscriptionType.property_marketing.key;
      const res: any = await SubscriptionApi.getSubscriptionsList({
        search: "",
        subscription_type: subscriptionType,
        listing_type: listingTypeKey,
        page: 1,
        limit: 30,
      });

      if (res?.data?.success) {
        setPlans(res?.data?.data?.docs || []);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to load plans.");
    } finally {
      setLoading(false);
    }
  };

  const getPricingDetails = (planData: any) => {
    if (!planData || !planData.stripe_product_prices) {
      return {
        price: planData?.price || 0,
        currency: "AUD",
        stripePriceId: "",
      };
    }
    const user: any = LocalStorageHelpers.getUserData();
    const finalCountryCode = detectedCountry || user?.country || "AU";
    let targetCurrency = "aud";
    if (finalCountryCode === "IN") targetCurrency = "inr";
    else if (finalCountryCode === "US") targetCurrency = "usd";

    const prices = planData.stripe_product_prices;
    let priceObj = prices.find((p: any) => p.currency === targetCurrency);
    if (!priceObj) priceObj = prices.find((p: any) => p.currency === "aud");
    if (!priceObj && prices.length > 0) priceObj = prices[0];

    if (priceObj) {
      return {
        price: priceObj.unit_amount / 100,
        currency: priceObj.currency.toUpperCase(),
        stripePriceId: priceObj.id,
      };
    }
    return { price: 0, currency: "AUD", stripePriceId: "" };
  };

  const successUrl = `${
    window.location.origin
  }/upgrade-property-plan/success?address=${encodeURIComponent(
    propertyAddress,
  )}&property_id=${propertyId}`;

  const handleSelectPlan = (plan: any) => {
    const user = LocalStorageHelpers.getUserData();
    if (!user?._id) {
      toast.error("User session invalid");
      return;
    }

    const usedCount = plan.current_user_usage_count || 0;
    const limit = plan.trial_properties || 0;

    const isEligible = plan.free_trial && usedCount < limit;

    setIsEligibleForTrial(isEligible);
    setSelectedPlanData(plan);
  };

  const handleFinalPayment = async (
    useWalletPoints: boolean,
    paymentSource: "agency" | "agent",
  ) => {
    if (!selectedPlanData) {
      toast.error("Please select a plan first");
      return;
    }

    const user = LocalStorageHelpers.getUserData();
    if (!user?._id) return toast.error("User session invalid");

    try {
      setProcessingPayment(true);

      const oldPlanPricing = getPricingDetails(currentPlan);
      const newPlanPricing = getPricingDetails(selectedPlanData);

      const payload = {
        agent_id: user._id,
        property_id: propertyId,
        old_plan_price: oldPlanPricing.price,
        new_plan_price: newPlanPricing.price,
        new_plan_id: selectedPlanData._id,
        property_address: propertyAddress,
        is_wallet_applied: useWalletPoints,
        success_redirect_url: successUrl,
        payment_method_source: paymentSource,
      };

      const res = await SubscriptionApi.upgradePropertyPlan(payload);

      if (res?.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error("Upgrade checkout failed");
      }
    } catch (err: any) {
      toast.error(err.message || "Upgrade failed");
    } finally {
      setProcessingPayment(false);
    }
  };

  const onClicksetToggle = (id: string) => {
    setSelectedFeatureId(selectedFeatureId === id ? "" : id);
  };

  return (
    <Modal
      open={open}
      onClose={(e, r) => r !== "backdropClick" && onClose()}
      className="property-plan-modal"
    >
      <div className="modal-scroll-container">
        <div className="modal-wrapper">
          <div className="modal-header-close">
            <IconButton className="close-btn" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </div>

          {selectedPlanData ? (
            <SelectPaymentMethod
              onBack={() => setSelectedPlanData(null)}
              propertyAddress={propertyAddress}
              walletBalance={userWalletBalance}
              selectedPlan={{
                planName: selectedPlanData.plan_name,
                price: getPricingDetails(selectedPlanData).price,
                currency: getPricingDetails(selectedPlanData).currency,
              }}
              isFreeTrial={isEligibleForTrial}
              usageCount={
                isEligibleForTrial
                  ? selectedPlanData.current_user_usage_count || 0
                  : selectedPlanData.current_user_discount_usage_count || 0
              }
              trialLimit={selectedPlanData.trial_properties || 0}
              discountPrice={selectedPlanData.discount_price}
              discountLimit={selectedPlanData.discount_properties}
              onConfirmPayment={(
                useWallet: boolean,
                paymentMethod: "agency" | "agent",
              ) => {
                handleFinalPayment(useWallet, paymentMethod);
              }}
              isAgencyPaymentAllowed={
                agentPermissions?.use_agency_payment_method ?? false
              }
            />
          ) : (
            <>
              <div className="text-center-header">
                <h2>Upgrade property marketing plan</h2>
                <p className="sub-text">
                  Select property marketing plan to upgrade
                </p>

                <p className="selecting-for-text">
                  Upgrading property marketing plan for
                </p>
                <p className="property-address">
                  {propertyAddress || "Address not available"}
                </p>
              </div>

              {loading || processingPayment ? (
                <div className="modal-loading-state">
                  <Loading />
                  {processingPayment && (
                    <p style={{ marginTop: 20, fontSize: 18, color: "#666" }}>
                      Processing request...
                    </p>
                  )}
                </div>
              ) : (
                <div className="plans-grid-container">
                  {plans.length === 0 ? (
                    <p>No plans available for this listing type.</p>
                  ) : (
                    plans.map((data) => {
                      const priceDetails = getPricingDetails(data);
                      return (
                        <div className="plan-card" key={data._id}>
                          <div className="card-body-contain">
                            <p className="card-title">{data.plan_name}</p>
                            <p className="subscriptions-type">
                              Property marketing
                            </p>

                            <div className="price-and-price-type">
                              {data?.discount_price ? (
                                <div className="original-price-contain">
                                  <div className="original-price-text-red-line"></div>
                                  <p className="original-price-text">{`AUD ${data?.price}`}</p>
                                </div>
                              ) : (
                                ""
                              )}

                              <div className="discount-price-contain">
                                <p className="discount-price-count">
                                  {/* {cardPriceDetails.currency}  */}
                                  AUD {data?.discount_price || data?.price}
                                </p>
                                <p className="discount-price-text">
                                  per <br />
                                  {data.price_type ===
                                  CommonEnums.priceType.per_property.key
                                    ? "property"
                                    : "month"}
                                </p>
                              </div>
                            </div>

                            <p className="property-contain">
                              {data?.discount_price
                                ? `For first ${data?.discount_properties} property then $${priceDetails.price}/property`
                                : `One time payment per property`}
                            </p>

                            <button
                              className="try-for-free-btn"
                              onClick={() => handleSelectPlan(data)}
                            >
                              {data.free_trial
                                ? `Try it free for first ${data.trial_properties} properties`
                                : "Select Plan"}
                            </button>

                            <div className="card-body-features-contain">
                              {data.plan_features.map((feature: any) => (
                                <div
                                  className="common-feature"
                                  key={feature._id}
                                >
                                  <div
                                    className="common-features-title-contain"
                                    onClick={() =>
                                      onClicksetToggle(feature._id)
                                    }
                                  >
                                    <div className="features-title-true-icon">
                                      <TrueRoundIcon />
                                    </div>
                                    <div className="features-title-right-contain">
                                      <p className="common-features-title">
                                        {feature.title}
                                      </p>
                                      <div
                                        className={`features-title-down-icon ${
                                          feature._id === selectedFeatureId
                                            ? "features-title-down-icon-rotate"
                                            : ""
                                        }`}
                                      >
                                        <DownArrowIcon />
                                      </div>
                                    </div>
                                  </div>
                                  <p
                                    className={`common-features-description ${
                                      feature._id === selectedFeatureId
                                        ? "description-animation"
                                        : ""
                                    }`}
                                  >
                                    {feature.description}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default PropertyPlanUpgradeModal;
